import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PasswordResetChat from './PasswordResetChat';
import { PASSWORD_RESET_CHAT_CONFIG } from './AIChatbot';

export default function LoginChatbotWidget({
  open: controlledOpen,
  onOpenChange,
  prefilledEmail = '',
  displayName = '',
  fabBottom = 28,
  fabRight,
  fabLeft,
  showFab = true,
}) {
  const useLeft = fabLeft != null;
  const fabHorizontal = useLeft
    ? { left: fabLeft }
    : { right: fabRight ?? 28 };
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = (value) => {
    if (onOpenChange) onOpenChange(value);
    else setInternalOpen(value);
  };

  const toggle = () => setOpen(!open);

  const widget = (
    <>
      {showFab ? (
      <motion.button
        type="button"
        onClick={toggle}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        title="Password Reset Assistant"
        style={{
          position: 'fixed', bottom: fabBottom, ...fabHorizontal, zIndex: 1000,
          width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: open
            ? '#EDF2F7'
            : 'linear-gradient(135deg, #0ea5e9 0%, #3B5BDB 100%)',
          boxShadow: open
            ? '0 2px 12px rgba(0,0,0,0.12)'
            : '0 6px 24px rgba(14,165,233,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s, box-shadow 0.2s',
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.svg
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.18 }}
              width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="#1A202C" strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </motion.svg>
          ) : (
            <motion.span
              key="lock"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ fontSize: 22, color: '#FFFFFF' }}
            >
              🔐
            </motion.span>
          )}
        </AnimatePresence>

        {!open && (
          <motion.div
            animate={{ scale: [1, 1.6], opacity: [0.5, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
            style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: '#0ea5e9', zIndex: -1,
            }}
          />
        )}
      </motion.button>
      ) : null}

      <AnimatePresence>
        {open && (
          <motion.div
            key="login-chatpanel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            style={{
              position: 'fixed', bottom: fabBottom + 64, ...fabHorizontal, zIndex: 999,
              width: 420, height: 'auto', maxHeight: 'min(480px, calc(100vh - 120px))',
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 20, overflow: 'hidden',
              boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #DDE1E7',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <PasswordResetChat
              prefilledEmail={prefilledEmail}
              displayName={displayName}
              title={PASSWORD_RESET_CHAT_CONFIG.title}
              subtitle={PASSWORD_RESET_CHAT_CONFIG.subtitle}
              avatarEmoji={PASSWORD_RESET_CHAT_CONFIG.avatarEmoji}
              accentGradient={PASSWORD_RESET_CHAT_CONFIG.accentGradient}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(widget, document.body);
}
